import { Utils } from '../../../../../utils/common'
import { logger } from '../../../../../utils/common/logger'
import { DOMEvents, DOMUtils } from '../../../../../utils/dom'
import { Store } from '../../../../../utils/storage/keys'
import { LocalStore } from '../../../../../utils/storage/local'
import { AnalyticsService } from '../../../../analytics'
import { IdentityEvent } from '../../../../common/events/identity'
import { IProfile } from '../../../../common/profile'
import SailthruAdapter from '../../sailthru'
import { LoginRadiusAdapter, LRErrorCode } from '../index'
import { ISuccessResponse } from '../widgets/login-widget'

export interface IWindow extends Window {
    Sailthru: any
    tp: any
}

declare let window: IWindow

export class LoginService {
    // Mobile app expects this parameter to contain the auth token
    // and be passed in the to-mobile-app redirection from the device's browser.
    public static readonly mobileRedirectTokenName = 'token'
    // Mobile application is expected to provide this parameter in the URL
    // when opening sign-in and registration pages.
    // If the param is present then we know we need to redirect there after
    // successful authentication.
    private static readonly mobileRedirectParamName = 'redirect_uri'

    private static readonly allowedMobileRedirects = [
        'calgaryherald://',
        'nationalpost://',
        'financialpost://',
        'edmontonjournal://',
        'vancouversun://',
        'londonfreepress://',
        'ottawacitizen://',
        'windsorstar://',
        'montrealgazette://',
        'leaderpost://',
        'starphoenix://',
    ]

    public static setMobileRedirectUrl() {
        if (sessionStorage) {
            const redirectUri = LoginService.getParamFromQueryString(LoginService.mobileRedirectParamName)
            if (LoginService.isMobileRedirect(redirectUri)) {
                sessionStorage.setItem(Store.MOBILE_REDIRECT_URI, redirectUri)
            }
        }
    }

    public static setRedirectUrl(url: string) {
        if (sessionStorage) {
            sessionStorage.setItem(Store.REDIRECT_URL, url)
        }
    }

    public static isMobileRedirect(url: string) {
        const mobile = (redirect: string) => url.startsWith(redirect)
        return url && LoginService.allowedMobileRedirects.some(mobile)
    }

    public static addMobileRedirectTokenToUrl(url: string, token: string) {
        const urlObj = new URL(url)
        urlObj.searchParams.append(LoginService.mobileRedirectTokenName, token)
        return urlObj.toString()
    }

    public static getMobileRedirectUrl() {
        let redirectUrl

        if (sessionStorage) {
            const url = sessionStorage.getItem(Store.MOBILE_REDIRECT_URI)
            if (LoginService.isMobileRedirect(url)) {
                redirectUrl = url
            }
        }

        return redirectUrl
    }

    /**
     * Get post auth redirect url.
     *
     * If set then the mobile redirect takes precedence over the traditional redirect.
     * Mobile redirect is intended to redirect the user to the mobile app and to provide
     * the auth token to the app. Because of that, if token is available, we append it
     * to the mobile redirection.
     *
     * @param defaultRedirect - if neither mobile nor traditional redirect is set then use this one
     * @param token - auth token to be appended to the mobile redirect
     * @returns
     */
    public static getRedirectUrl(defaultRedirect: string, token?: string) {
        let redirectUrl = LoginService.getMobileRedirectUrl()
        if (redirectUrl && token) {
            // On mobile devices we redirect from the device's browser, where
            // user has authenticated, to the mobile application.
            // We use so called deep link for that and in this case we need to
            // pass the auth token as the redirection param.
            try {
                redirectUrl = LoginService.addMobileRedirectTokenToUrl(redirectUrl, token)
            } catch (e) {
                logger('error', 'Error adding token to the mobile redirect link.', e)
            }
        }

        if (!redirectUrl && sessionStorage) {
            redirectUrl = sessionStorage.getItem(Store.REDIRECT_URL)
        }

        return redirectUrl || defaultRedirect
    }

    public static getSSORedirectURL(token: string, redirectUrl: string) {
        const ssoRedirectURL = new URL(LoginRadiusAdapter.ssoRedirectLink)
        const callbackURL = redirectUrl ? redirectUrl : location.origin
        ssoRedirectURL.searchParams.append('token', token)
        ssoRedirectURL.searchParams.append('callback', callbackURL)
        return ssoRedirectURL.toString()
    }

    public static async handleLoginSuccess(response: ISuccessResponse, isSocialSignIn = false) {
        window.tp = window.tp || []
        const token = response.access_token
        const jwtToken = response.jwttoken

        // store token
        localStorage.setItem(Store.JWT, jwtToken)

        // LR returns Data or Profile in the login success response object.
        const data = response.Data || response.Profile

        window.tp.push(['init', () => {
            // Setting the new JWT token for authenticated user
            window.tp.push(['setExternalJWT', jwtToken])
        }])

        const profileData = LoginService.createProfile(data)

        // Get stored redirect URL from where user navigated to the sign in page
        let redirectUrl = LoginService.getRedirectUrl('', token)
        let finalizeLoginTimeout: number
        /**
         * The cbFinalizeLogin function may be called:
         * - directly
         * - by an event handler
         * - by a timeout that executes in case the event handler is not called the function.
         *
         * Because of the above we need to clear the timeout as soon
         * as the function is called to prevent it from being
         * called again by the timeout - in case it was called directly or by an event handler.
         */
        const cbFinalizeLogin = async () => {
            clearTimeout(finalizeLoginTimeout)
            let signInMethod = response.Profile.RegistrationProvider

            if (signInMethod === 'live') {
                // change sign in method name to microsoft
                signInMethod = 'microsoft'
            }

            if (response.Profile.NoOfLogins === 1) {
                LoginService.sendRegistrationAnalytics(signInMethod || 'traditionalRegister')
            } else {
                LoginService.sendLoginAnalytics(signInMethod || 'traditionalSignin')
            }
            try {
                await SailthruAdapter.setCookie(profileData.email)
            } catch (e) {
                logger('error', e)
            }
            const pianoParams = sessionStorage && sessionStorage.getItem(Store.PIANO_PARAMS)
            if (pianoParams) {
                // If params object is valid and the user is logged in - start checkout
                window.tp.push(['init', () => {
                    window.tp.offer.startCheckout(JSON.parse(pianoParams))
                }])
                // sessionStorage.removeItem(Store.PIANO_PARAMS)
            } else {
                 location.replace(LoginService.getSSORedirectURL(token, redirectUrl))
            }
        }

        const fireIdentityEvents = () => {
            document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, {
                ...profileData,
                isLogin: true
            }))

            document.dispatchEvent(new CustomEvent(IdentityEvent.IDENTITY_LOGIN, {
                detail: {
                    email: profileData.email,
                    uuid: profileData.uuid
                }
            }))
        }

        if (!isSocialSignIn || this.isAppleRegistration()) {
            fireIdentityEvents()
            location.replace(LoginService.getSSORedirectURL(token, redirectUrl))
        } else {
            /*
            Wait for mParticle services initialize and send login/registration analytics event.

            We set the fallback timeout here to call the login finalization function
            in case MP event is not triggered.

            Having user logged in is more important here than mParticle events.
            Note: This is not an optimal solution but rather a hackish safety precaution.
            We should have mParticle and other third party events to be an addition to the
            main flows and not a driver of them. Otherwise we're risking breking the functionality
            because of faulty analytics etc.
            */
            const finalizationTimeoutTime = 3000
            finalizeLoginTimeout = window.setTimeout(() => cbFinalizeLogin(), finalizationTimeoutTime)
            document.addEventListener(IdentityEvent.MP_LOGIN_FINISHED, cbFinalizeLogin)
            document.addEventListener(IdentityEvent.MP_LISTENS, () => {
                document.removeEventListener(IdentityEvent.MP_LISTENS, fireIdentityEvents)
                fireIdentityEvents()
            })
        }

        // add an event listener for profile updated
        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, async () => {
            const fromActivateButton = sessionStorage ? sessionStorage.getItem(Store.ACTIVATE_BUTTON) : undefined

            if (fromActivateButton) {
                sessionStorage.removeItem(Store.ACTIVATE_BUTTON)
                const profile = LocalStore.get<IProfile>(Store.ID)
                const printSubscriber = profile.printSubscriber

                if (printSubscriber) {
                    // set redirect to already activated page
                    redirectUrl = `${location.origin}/already-activated`
                }
            }

            // set sailthru cookie and redirect back to site
            if (!isSocialSignIn || this.isAppleRegistration()) {
                await cbFinalizeLogin()
            }
        })
    }

    /**
     * Clear error messages
     */
    public static clearErrors() {
        const errorMsg = document.querySelector(`.${LoginRadiusAdapter.DOM.class.errorMessageContainer}`)
        if (errorMsg) {
            errorMsg.remove()
        }

        // clear input errors
        const inputErrorWrappers = document.querySelectorAll(`.${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

        inputErrorWrappers.forEach((inputErrorWrapper) => {
            const inputField = inputErrorWrapper.querySelector('input')
            const inputError = inputErrorWrapper.querySelector(`.${LoginRadiusAdapter.DOM.class.inputErrorMessage}`)

            DOMUtils.removeClass(inputErrorWrapper, `${LoginRadiusAdapter.DOM.class.inputWrapperError}`)

            if (inputField) {
                inputField.removeAttribute('aria-describedby')
                inputField.removeAttribute('aria-invalid')
            }

            if (inputError) {
                inputError.remove()
            }
        })
    }

    /**
     * Display any errors that appear after submitting the form
     * @param errors any
     */
    public static showErrors(errors: any) {
        if (Array.isArray(errors)) {
            const isApiErrorArray = !!(errors.length && errors[0].ErrorCode)

            if (isApiErrorArray) {
                const errorDiv = document.createElement('div')
                const errorList = document.createElement('ul')
                DOMUtils.addClass(errorList, 'errorlist')

                // Show api errors
                const identityScreenForm = document.querySelector(`.${LoginRadiusAdapter.DOM.class.identityForm}`)
                errorDiv.setAttribute('role', 'alert')
                errorDiv.className = LoginRadiusAdapter.DOM.class.errorMessageContainer

                const error = errors[0]
                const errorListItem = document.createElement('li')

                if (error.Description) {
                    if (error.ErrorCode === LRErrorCode.VERIFY_EMAIL_FIRST) {
                        // Verify email address social sign in error
                        const errorListSpan = document.createElement('span')
                        const errorLink = document.createElement('a')

                        errorLink.className = 'identity-verify-email'
                        errorLink.href = '/verify-email/'
                        errorLink.innerText = 'Resend Verify Email?'
                        errorLink.onclick = () => {
                            LoginService.sendClickEvent(
                                errorLink.innerText,
                                'link',
                                'social-sign-in',
                                errorLink.href,
                            )
                        }

                        errorListSpan.innerHTML = `${error.Description} `
                        errorListSpan.appendChild(errorLink)

                        errorListItem.appendChild(errorListSpan)
                    } else {
                        errorListItem.innerHTML = error.Description
                    }

                    errorList.appendChild(errorListItem)
                }

                if (identityScreenForm) {
                    errorDiv.appendChild(errorList)
                    identityScreenForm.after(errorDiv)
                    // If there are content blocker errors then scroll the view down to the blocker
                    // Scroll immediately if the blocker is already visible.
                    identityScreenForm.scrollIntoView({block: 'center'})
                    // Scroll later, using observer - for situations when blocker is shown later by Piano scripts.
                    const contentBlocker = document.querySelector(`.${LoginRadiusAdapter.DOM.id.contentBlocker}`)
                    const observer = new MutationObserver((_mutations) => {
                        // What Piano Composer does is a removal of the `hidden` class
                        if (!contentBlocker.classList.contains('hidden')) {
                            identityScreenForm.scrollIntoView({block: 'center'})
                        }
                    })
                    observer.observe(contentBlocker, {attributes: true, attributeFilter:['class']})
                }
            } else {
                // Show input errors
                errors.map((error) => {
                    if (error.Id && error.Message) {
                        const inputField = document.querySelector(`#${error.Id}`)
                        const inputErrorSpan = document.createElement('span')
                        const inputErrorList = document.createElement('ul')
                        const errorListItem = document.createElement('li')
                        const errorMessageId = `${error.Id}__error`
                        let ariaDescribedBy = inputField.getAttribute('aria-describedBy')

                        if (!ariaDescribedBy) {
                            ariaDescribedBy = `${errorMessageId}`
                        }

                        inputField.setAttribute('aria-describedBy', ariaDescribedBy)
                        inputField.setAttribute('aria-invalid', 'true')
                        inputErrorSpan.id = errorMessageId
                        errorListItem.innerHTML = error.Message

                        // Error field styling classes
                        DOMUtils.addClass(inputErrorSpan, LoginRadiusAdapter.DOM.class.inputErrorMessage)
                        DOMUtils.addClass(inputErrorList, 'errorlist')

                        // Add validation error
                        const inputWrapper = inputField.parentElement
                        DOMUtils.addClass(inputWrapper, LoginRadiusAdapter.DOM.class.inputWrapperError)
                        inputErrorList.appendChild(errorListItem)
                        inputErrorSpan.appendChild(inputErrorList)
                        inputWrapper.appendChild(inputErrorSpan)
                    }
                })

                // tslint:disable-next-line: max-line-length
                const firstInputError = document.querySelector(`.${LoginRadiusAdapter.DOM.class.inputWrapperError} input`) as HTMLInputElement

                if (firstInputError) {
                    firstInputError.focus()
                }
            }
        }
    }

    /**
     * Set the back to site link to the redirect url in session storage
     * Redirects to hosted solution if there is a token
     *
     * @param {string} token
     */
    public static setRedirectLink(token?: string) {
        const backToSiteButton = document.querySelector('.button__back-to-site') as HTMLAnchorElement

        let redirectUrl

        if (sessionStorage) {
            if (token) {
                const callbackUrl = LoginService.getRedirectUrl(location.origin, token)
                redirectUrl = LoginService.getSSORedirectURL(token, callbackUrl)
            } else {
                redirectUrl = LoginService.getRedirectUrl('')
            }
        }

        if (backToSiteButton && redirectUrl) {
            backToSiteButton.href = redirectUrl
        }
    }

    /**
     * Send login analytics
     * @param {string} signInMethod: 'social' or 'traditionalSignin'
     */
    public static sendLoginAnalytics(signInMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            sign_in_method: 'sign_in_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signin,
                integration_name: 'LoginRadius',
                sign_in_method: signInMethod
            }
        }))
    }

    /**
     * Send registration analytics event
     * @param {string} registrationMethod: 'social' or 'traditionalSignin'
     */
    public static sendRegistrationAnalytics(registrationMethod: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name',
                            registration_method: 'registration_method'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.registration,
                integration_name: 'LoginRadius',
                registration_method: registrationMethod
            }
        }))
    }

    /**
     * Send signout analytics
     */
    public static sendLogoutAnalytics() {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            integration_name: 'integration_name'
                        },
                        mp_event_type: 'UserContent'
                    }
                },
                event_name: AnalyticsService.customEventNames.signout,
                integration_name: 'LoginRadius',
            }
        }))
    }

    /**
     * Send submission error analytics on form submission
     *
     * @param string errors
     * @param string formName
     */
    public static sendSubmissionErrorAnalytics(errors: any, formName: string) {
        if (Array.isArray(errors) && errors.length) {
            // API error
            if (errors[0].ErrorCode) {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    error_code: 'error_code',
                                    error_message: 'error_message',
                                    form_name: 'form_name',
                                },
                                mp_event_type: 'UserContent'
                            }
                        },
                        error_code: `${errors[0].ErrorCode}`,
                        error_message: errors[0].Description,
                        event_name: AnalyticsService.customEventNames.submissionError,
                        form_name: formName,
                    }
                }))
            } else {
                // input field error
                errors.map((error) => {
                    document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                        detail: {
                            control_fields: {
                                mparticle: {
                                    keys: {
                                        error_message: 'error_message',
                                        form_name: 'form_name',
                                        input_id: 'input_id',
                                    },
                                    mp_event_type: 'UserContent'
                                }
                            },
                            error_message: error.Message,
                            event_name: AnalyticsService.customEventNames.submissionError,
                            form_name: formName,
                            input_id: error.Id,
                        }
                    }))
                })
            }
        }
    }

    /**
     * Send form submission analytics event
     *
     * @param string formName
     */
    public static sendFormSubmissionEvent(formName: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                control_fields: {
                    mparticle: {
                        keys: {
                            form_name: 'form_name',
                        },
                        mp_event_type: 'Other'
                    }
                },
                event_name: AnalyticsService.customEventNames.formSubmission,
                form_name: formName,
            }
        }))
    }

    /**
     * Send form interaction event
     *
     * @param string formWrapper
     * @param string formName
     */
    public static sendFormInteractionEvent(formWrapper: string, formName: string) {
        const form = document.querySelector(`#${formWrapper} form`)

        if (form) {
            form.addEventListener(DOMEvents.FOCUS_IN, () => {
                document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                    detail: {
                        control_fields: {
                            mparticle: {
                                keys: {
                                    form_name: 'form_name',
                                },
                                mp_event_type: 'Other'
                            }
                        },
                        event_name: AnalyticsService.customEventNames.formInteraction,
                        form_name: formName,
                    }
                }))
            }, {once: true})
        }
    }

    /**
     * Send click event
     *
     * @param string anchorText
     * @param string clickSourceType
     * @param string layoutSection
     * @param string targetUrl
     */
    public static sendClickEvent(anchorText: string, clickSourceType: string,
                                 layoutSection: string, targetUrl: string) {
        document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
            detail: {
                action: DOMEvents.click,
                anchor_text: anchorText,
                click_source_type: clickSourceType,
                control_fields: {
                    mparticle: {
                        keys: {
                            anchor_text: 'anchor_text',
                            click_source_type: 'click_source_type',
                            layout_section: 'layout_section',
                            target_url: 'target_url'
                        },
                        mp_event_type: 'Navigation'
                    }
                },
                event_name: AnalyticsService.customEventNames.click,
                layout_section: layoutSection,
                target_url: targetUrl
            }
        }))
    }

    /**
     * Shapes the LoginRadius response into a profile object
     */
    public static createProfile(data: any) {
        return {
            created: data.CreatedDate,
            doNotContact: undefined,
            email: (Array.isArray(data.Email)) ? data.Email[0].Value : '',
            emailConfirmed: data.EmailVerified,
            familyName: data.LastName,
            gender: data.Gender,
            givenName: data.FirstName,
            lastSignIn: data.LastLoginDate,
            name: data.FullName,
            uuid: data.Uid
        } as IProfile
    }

    /**
     * Change footer links to redirect to hosted solution
     *
     * @param {string} token
     */
     public static setSSOFooterLinks(token: string) {
        document.querySelectorAll('.identity-footer a').forEach((link: HTMLAnchorElement) => {
            let redirectUrl
            if (link.href.indexOf('http') !== -1) {
                redirectUrl = link.href
            } else {
                redirectUrl = `${location.origin}${link.href}`
            }
            link.href = LoginService.getSSORedirectURL(token, redirectUrl)
        })
    }

    public static isAppleRegistration(): boolean {
        return document.getElementById('loginradius-submit-login') !== null &&
            document.getElementById('loginradius-submit-register') !== null ||
             document.querySelectorAll('#loginradius-submit-login').length > 1
    }

    private static getParamFromQueryString(param: string): string {
        const urlParams = new URLSearchParams(window.location.search)
        return urlParams.get(param)
    }
}
